import { Avatar, Button, EmptyState, cx } from '@cartly/ui';
import {
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  Moon,
  ShieldAlert,
  ShieldCheck,
  Sun,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { Logo } from '../../components/Logo';
import { useActiveSession, useSession } from '../../app/session';
import { useTheme, type ThemePreference } from '../../app/theme';
import { SUPER_ADMIN_NAV } from './nav';

const COLLAPSE_KEY = 'cartly.superadmin.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

const THEMES: { value: ThemePreference; label: string; icon: ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun size={14} /> },
  { value: 'dark', label: 'Dark', icon: <Moon size={14} /> },
  { value: 'system', label: 'System', icon: <Monitor size={14} /> },
];

function Sidebar({
  collapsed,
  onToggle,
  onNavigate,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  return (
    <aside className="sidebar" aria-label="Super Admin navigation">
      <div className="sidebar__brand">
        <Logo variant="wordmark" surface="dark" className="sidebar__wordmark" />
        <Logo variant="mark" surface="dark" className="sidebar__mark" />
      </div>
      <nav className="sidebar__nav">
        {SUPER_ADMIN_NAV.map((section) => (
          <div key={section.title ?? 'main'}>
            {section.title && <div className="sidebar__section">{section.title}</div>}
            {section.items.map((item) => {
              const Icon = item.icon;
              if (!item.path) {
                // Not built yet: shown so the whole panel is visible, but not clickable.
                return (
                  <div
                    key={item.label}
                    className="sidebar__link sidebar__link--muted"
                    aria-disabled="true"
                    title={collapsed ? `${item.label} (soon)` : undefined}
                  >
                    <Icon className="sidebar__link-icon" size={18} />
                    <span className="sidebar__link-label">{item.label}</span>
                    <span className="sidebar__pill">Soon</span>
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end
                  title={collapsed ? item.label : undefined}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cx('sidebar__link', isActive && 'sidebar__link--active')
                  }
                >
                  <Icon className="sidebar__link-icon" size={18} />
                  <span className="sidebar__link-label">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="sidebar__footer">
        <button
          type="button"
          className="sidebar__collapse"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          <span className="sidebar__collapse-label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}

function Forbidden({ mfaMissing }: { mfaMissing: boolean }) {
  const { state } = useSession();
  const hasTenant = state.status === 'active' && state.session.activeTenant !== null;
  return (
    <div className="page-center">
      <EmptyState
        icon={<ShieldAlert size={28} />}
        title={mfaMissing ? 'Enable two-factor authentication' : 'Super Admin access required'}
        action={
          <Link to={mfaMissing && hasTenant ? '/settings/security' : '/'} className="btn">
            {mfaMissing && hasTenant ? 'Open security settings' : 'Back to the dashboard'}
          </Link>
        }
      >
        {mfaMissing
          ? 'The Super Admin panel needs an enrolled authenticator app. Enable it under Settings, Security, then come back.'
          : 'This area is only for Cartly360 platform administrators.'}
      </EmptyState>
    </div>
  );
}

/**
 * Shell for the platform administration panel. The checks here only decide what to show: every
 * `/api/v1/admin/*` call is authorized again on the server.
 */
export function SuperAdminShell() {
  const session = useActiveSession();
  const { logout } = useSession();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!session.user.isPlatformAdmin) return <Forbidden mfaMissing={false} />;
  if (!session.user.mfaEnabled) return <Forbidden mfaMissing />;

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      } catch {
        // Preference not persisted.
      }
      return !v;
    });
  };

  return (
    <div
      className={cx(
        'app-shell',
        collapsed && 'app-shell--collapsed',
        drawerOpen && 'app-shell--drawer-open',
      )}
    >
      <Sidebar
        collapsed={collapsed}
        onToggle={toggleCollapsed}
        onNavigate={() => setDrawerOpen(false)}
      />
      <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
      <div className="app-main">
        <header className="topbar">
          <Button
            variant="ghost"
            iconOnly
            className="topbar__menu-button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            icon={<Menu size={18} />}
          />
          <span className="superadmin-badge">
            <ShieldCheck size={14} /> Super Admin
          </span>
          <div className="topbar__spacer" />
          {session.activeTenant && (
            <Link to="/" className="btn btn--ghost btn--sm" aria-label="Merchant dashboard">
              <LayoutDashboard size={14} />
              <span className="hide-on-mobile">Merchant dashboard</span>
            </Link>
          )}
          <div className="segmented" role="radiogroup" aria-label="Theme">
            {THEMES.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={theme === o.value}
                aria-label={o.label}
                title={o.label}
                className={cx(
                  'segmented__option',
                  theme === o.value && 'segmented__option--active',
                )}
                onClick={() => setTheme(o.value)}
              >
                {o.icon}
              </button>
            ))}
          </div>
          <span className="hide-on-mobile">
            <Avatar name={session.user.name} />
          </span>
          <Button
            variant="ghost"
            iconOnly
            aria-label="Sign out"
            onClick={() => void logout().then(() => navigate('/login'))}
            icon={<LogOut size={16} />}
          />
        </header>
        <main className="app-content" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
